import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
} from '@angular/core';
import { fade, openClose } from '@core/animations/animations';
import { Address, DefaultAddress } from '@models/address';
import {
	AssociatedContactType,
	EstateStatusType,
	GenderOptions,
	GenderType,
	PersonClient,
	RepresentationType,
	ResidentStatusOptions,
	ResidentStatusType,
	TitleOptions,
	TitleType,
} from '@models/clients';
import {
	BusinessMatterClientWithAssociations,
	MatterFileType,
	PersonMatterClientWithAssociations,
	SalutationOptionType,
} from '@models/matter';
import { ClientsDispatch } from '@state/clients';
import { DropdownResult } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { ICON } from '@core/util/icon';
import { SalutationsService } from 'src/app/shared/services/salutations/salutations.service';
import { mapTitleToGender } from '@core/util/client';
import { YES_NO_OPTIONS } from 'src/app/ui/utils/constants';
import { deleteField } from '@angular/fire/firestore';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { MatterClients } from '@state/global/matter_clients';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { uniq } from 'lodash';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { Associations } from '../../global-profile.types';
import { TestHooks } from './person-details.hooks';

@UntilDestroy()
@Component({
	selector: 'app-person-details',
	templateUrl: './person-details.component.html',
	styleUrls: ['./person-details.component.scss'],
	animations: [fade(), openClose()],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class PersonDetailsComponent implements OnInit {
	testHooks = TestHooks;

	@Input() set client(client: PersonClient) {
		if (client) {
			this._client = client;
			this.sameReportAddress = !this._client?.reportAddress;
			this.primarySalutation =
				this.salutationService.getPersonPrimarySalutation(client);
			this.envelopeSalutation =
				this.salutationService.getPersonEnvelopeSalutation(client);
		}
	}
	@Input() associations: Associations;

	@ViewSelectSnapshot(MatterClients.get_primaryClient) primaryClient;
	@ViewSelectSnapshot(SelectedMatter.address) address: Address;

	@ViewSelectSnapshot(SelectedMatter.id) matterId: string;

	@Select(MatterClients.get_matterClientsWithAssociationMapped)
	matterClients$: Observable<
		(
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[]
	>;

	public _client: PersonClient;
	public fileTypes = MatterFileType;
	public representationTypes = RepresentationType;
	public estateStatusTypes = EstateStatusType;
	public salutationTypes = SalutationOptionType;
	public primarySalutation = '';
	public envelopeSalutation = '';
	public associatedContactTypes = AssociatedContactType;
	public allClientsHaveSameReportAddress = false;
	public allClientsHaveSameServiceAddress = false;

	public matterClients: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[] = [];

	public genderOptions = GenderOptions;
	public residentStatusOptions = ResidentStatusOptions;
	public sameReportAddress;
	public ICON = ICON;

	public titleOptions = [
		{
			items: TitleOptions,
		},
	];

	public yesNoOptions = YES_NO_OPTIONS;

	get contactsLocked() {
		return (
			this._client.representation === RepresentationType.ESTATE ||
			this._client.representation === RepresentationType.ATTORNEY
		);
	}

	get contactsDeceased() {
		return this._client.representation === RepresentationType.ESTATE;
	}

	get contactsDeceasedSubtitle() {
		if (!this.contactsLocked) return '';
		return this.contactsDeceased
			? "This contact is deceased, so these fields aren't editable."
			: 'Contact details are locked due to Power of Attorney selection';
	}

	get contactIcon() {
		return this.contactsLocked ? ICON.LOCK : undefined;
	}

	get addAssociateType(): AssociatedContactType {
		switch (this._client?.representation) {
			case RepresentationType.ATTORNEY:
				return AssociatedContactType.POWER_OF_ATTORNEY;
			case RepresentationType.ESTATE:
				return AssociatedContactType.ESTATE_TRUSTEE;
			default:
				return null;
		}
	}

	constructor(
		private clientsDispatch: ClientsDispatch,
		private salutationService: SalutationsService,
	) {}

	ngOnInit(): void {
		this.matterClients$
			.pipe(untilDestroyed(this))
			.subscribe((_matterClients) => {
				this.matterClients = _matterClients;
				const reportAddressStreetNames = _matterClients.map(
					(x) => x?.reportAddress?.street,
				);
				const serviceAddressStreetNames = _matterClients.map(
					(x) => x.serviceAddress?.street,
				);

				this.allClientsHaveSameReportAddress =
					uniq(reportAddressStreetNames)?.length === 1;
				this.allClientsHaveSameServiceAddress =
					uniq(serviceAddressStreetNames)?.length === 1;
			});
	}

	showReportAddress(sameReportAddressAsService: boolean) {
		if (sameReportAddressAsService) {
			this.clientsDispatch.OverwritePersonClientFields(
				this._client.id,
				'reportAddress',
				deleteField(),
			);
			this.sameReportAddress = true;
		} else {
			if (this._client.reportAddress) {
				this._client.reportAddress = DefaultAddress;
				this.clientsDispatch.OverwritePersonClientFields(
					this._client.id,
					'reportAddress',
					DefaultAddress,
				);
			}
			this.sameReportAddress = false;
		}
	}

	setAllClientToUseReportAddress() {
		this.matterClients
			.filter((x) => x.id !== this._client.id)
			.forEach((_matterClient) => {
				this.reportAddressChanged(_matterClient.id, this._client.reportAddress);
			});
	}

	reportAddressChanged(clientId: string, reportAddress: Address): void {
		this.clientsDispatch.CreateOrMerge(clientId, { reportAddress });
	}

	serviceAddressChanged(clientId: string, serviceAddress: Address): void {
		this.clientsDispatch.CreateOrMerge(clientId, { serviceAddress });
	}

	setAllClientToUseServiceAddress() {
		this.matterClients
			.filter((x) => x.id !== this._client.id)
			.forEach((_matterClient) => {
				this.serviceAddressChanged(
					_matterClient.id,
					this._client.serviceAddress,
				);
			});
	}

	onTitleChanged = (title: string) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'title',
			title,
		);
	};

	onTitleSelected = (event: DropdownResult<{ name: TitleType }>) => {
		this.onTitleChanged(event.result.name);
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'gender',
			mapTitleToGender(event.result.name),
		);
	};

	onFirstNameChanged = (event: string) => {
		if (event?.length > 0) {
			this.clientsDispatch.OverwritePersonClientFields(
				this._client.id,
				'firstName',
				event,
			);
		}
	};

	setOccupation(occupation: string) {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'occupation',
			occupation,
		);
	}

	setBusinessAddress(businessAddress: Address) {
		this.clientsDispatch.CreateOrMerge(this._client.id, { businessAddress });
	}

	onMiddleNamesChanged = (event: string) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'middleNames',
			event,
		);
	};

	onLastNameChanged = (event: string) => {
		if (event?.length > 0) {
			this.clientsDispatch.OverwritePersonClientFields(
				this._client.id,
				'lastName',
				event,
			);
		}
	};

	onPreferredNameChanged = (event: string) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'preferredName',
			event,
		);
	};

	onResidentChange(event: boolean) {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'resident',
			event,
		);
	}

	onResidentStatusChange(event: ResidentStatusType) {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'residentStatus',
			event,
		);
	}

	onGenderSelected(event: GenderType) {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'gender',
			event,
		);
	}

	onRepresentationSelected(event: RepresentationType) {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'representation',
			event,
		);
	}

	onEstateStatusSelected = (event: EstateStatusType) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'estateStatus',
			event,
		);
	};

	onPowerOfAttorneyDateChanged = (event: string) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'powerOfAttorneyDate',
			event,
		);
	};

	onInstrumentNumberChanged = (event: string) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'instrumentNumber',
			event,
		);
	};

	onCellChanged = (event: string) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'phone',
			event,
		);
	};

	onHomeChanged = (event: string) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'home',
			event,
		);
	};

	onWorkChanged = (event: string) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'work',
			event,
		);
	};

	onEmailChanged = (event: string) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'email',
			event,
		);
	};

	onReLineChanged = (event: string) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'reLine',
			event,
		);
	};

	onPrimarySalutationTypeChanged = (event: SalutationOptionType) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'primarySalutationOption',
			event,
		);
	};

	onPrimarySalutationChanged = (event: string) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'customPrimarySalutation',
			event,
		);
	};

	onEnvelopeSalutationTypeChanged = (event: SalutationOptionType) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'envelopeSalutationOption',
			event,
		);
	};

	onEnvelopeSalutationChanged = (event: string) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'customEnvelopeSalutation',
			event,
		);
	};

	onEnvelopeSalutationContChanged = (event: string) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'envelopeSalutationContd',
			event,
		);
	};

	setServiceAddress = (serviceAddress: Address) => {
		this.clientsDispatch.CreateOrMerge(this._client.id, { serviceAddress });
	};

	setReportAddress = (reportAddress: Address) => {
		this.clientsDispatch.CreateOrMerge(this._client.id, { reportAddress });
	};

	setDateOfBirth = (dateOfBirth: string) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'dateOfBirth',
			dateOfBirth,
		);
	};

	setDateOfDeath = (dateOfDeath: string) => {
		this.clientsDispatch.OverwritePersonClientFields(
			this._client.id,
			'dateOfDeath',
			dateOfDeath,
		);
	};
}
